// Story 5.6 — TanStack Query mutation hook for POST
// /api/v1/marketplace/listings/share/{listing_id}/cancel.
//
// Mirror of useBuyShareListing (Story 5.5) in shape but the success
// envelope only carries listing_id + share_id + status + cancelled_at.
//
//   * Generates an Idempotency-Key per call (Story 1.6 contract; the
//     middleware caches the success envelope so a network-retry replay
//     returns the cached 200 instead of surfacing a 409 LISTING_NOT_ACTIVE).
//   * Branched error envelope per `code`:
//       - LISTING_NOT_FOUND (404) → missing OR not yours (single-bucket
//         enumeration defense — non-owners cannot probe which listings
//         exist).
//       - LISTING_NOT_ACTIVE (409) → already sold/cancelled/expired.
//       - UNAUTHORIZED (401) → stale session.
//       - RATE_LIMITED (429) → caller burned the 30/min cancel bucket.
//
// NO onSuccess cache invalidation in the hook itself — the consumer
// (CancelListingConfirmDialog / MyListingsPage / showUndoListingToast)
// owns invalidation because it knows which queries to refresh.

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface CancelShareListingVariables {
  listing_id: string;
}

export interface CancelShareListingResponseData {
  listing_id: string;
  share_id: string;
  status: string;
  cancelled_at: string;
}

export interface CancelShareListingError {
  code: string;
  message: string;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postCancelShareListing(
  variables: CancelShareListingVariables,
): Promise<CancelShareListingResponseData> {
  const response = await fetch(
    `/api/v1/marketplace/listings/share/${variables.listing_id}/cancel`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": freshIdempotencyKey(),
      },
      body: "{}",
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CancelShareListingError = {
      code: "CANCEL_LISTING_FAILED",
      message: `Cancel failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: CancelShareListingResponseData };
  return body.data;
}

export function useCancelShareListing(): UseMutationResult<
  CancelShareListingResponseData,
  CancelShareListingError,
  CancelShareListingVariables
> {
  return useMutation<
    CancelShareListingResponseData,
    CancelShareListingError,
    CancelShareListingVariables
  >({
    mutationFn: postCancelShareListing,
    // NO onSuccess — caller invalidates the relevant query keys.
  });
}
